import { useState, useEffect, useMemo, useRef } from "react";
import { Node } from "reactflow";

interface TitleValidationReturn {
  titleError: string | null;
  setTitleError: React.Dispatch<React.SetStateAction<string | null>>;
  isValid: boolean;
}

/**
 * Custom hook for validating node titles
 * Checks for duplicates only when exact complete match exists
 * Uses debouncing to avoid showing errors while actively typing
 * Optimized to prevent unnecessary rerenders
 *
 * @param currentNodeId - The ID of the current node
 * @param title - The current title value (local state)
 * @param nodes - All nodes in the flow
 * @returns Object containing titleError state and validation status
 */
export const useTitleValidation = (
  currentNodeId: string,
  title: string,
  nodes: Node[],
): TitleValidationReturn => {
  const [titleError, setTitleError] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const normalizeTitle = (value: unknown): string => {
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
    return "";
  };

  // Memoize nodes list based on actual changes (not reference changes)
  const nodesTitlesMap = useMemo(() => {
    const map = new Map<string, string>();
    nodes.forEach((node) => {
      const nodeTitleRaw =
        (node.data as any).title || (node.data as any).header;
      const nodeTitle = normalizeTitle(nodeTitleRaw).trim().toLowerCase();
      if (nodeTitle && node.id !== currentNodeId) {
        map.set(node.id, nodeTitle);
      }
    });
    return map;
  }, [
    nodes
      .map((n) => {
        const rawTitle = (n.data as any).title || (n.data as any).header;
        return `${n.id}:${normalizeTitle(rawTitle)}`;
      })
      .join("|"),
    currentNodeId,
  ]);

  // Re-validate title with debouncing (only check after user stops typing)
  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Clear error immediately when user starts typing (provides instant feedback)
    setTitleError(null);

    // Empty title check - don't validate empty titles
    const safeTitle = normalizeTitle(title);
    if (!safeTitle.trim()) {
      return;
    }

    // Debounce validation - only validate 300ms after user stops typing
    timeoutRef.current = setTimeout(() => {
      const trimmedTitle = safeTitle.trim().toLowerCase();

      // Check for EXACT complete match only (case-insensitive) using memoized map
      let duplicateFound = false;
      for (const nodeTitle of nodesTitlesMap.values()) {
        // IMPORTANT: Only exact match - no partial checking
        // "1" will NOT match "1234", "test" will NOT match "testing"
        if (nodeTitle === trimmedTitle) {
          duplicateFound = true;
          break;
        }
      }

      if (duplicateFound) {
        const errorMsg = `A node with the title "${safeTitle.trim()}" already exists. Please choose a unique title.`;
        setTitleError(errorMsg);
      } else {
        setTitleError(null);
      }
    }, 300); // Wait 300ms after user stops typing

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [title, nodesTitlesMap]);

  return {
    titleError,
    setTitleError,
    isValid: !titleError && normalizeTitle(title).trim().length > 0,
  };
};
